import { NavLink, Stack, Text } from "@mantine/core";
import { IconLayoutDashboard, IconAlertTriangle, IconShieldCheck, IconChecklist, IconUsers, IconHistory, IconBriefcase, IconBoxMultiple, IconAdjustmentsHorizontal, IconBook, IconFileText } from "@tabler/icons-react";
import { NavLink as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext.js";
import { APP_VERSION } from "../version.js";

interface Item { to: string; label: string; icon: React.ReactNode; adminOnly?: boolean }

export function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const visible = (i: Item) => !i.adminOnly || user?.role === "admin";

  const ITEMS: Item[] = [
    { to: "/", label: t("nav.item.overview"), icon: <IconLayoutDashboard size={18} /> },
    { to: "/ereignisse", label: t("nav.item.events"), icon: <IconAlertTriangle size={18} /> },
    { to: "/risiken", label: t("nav.item.risks"), icon: <IconShieldCheck size={18} /> },
    { to: "/massnahmen", label: t("nav.item.measures"), icon: <IconChecklist size={18} /> },
  ];
  const CATALOGS: Item[] = [
    { to: "/bedrohungen", label: t("nav.item.threats"), icon: <IconBoxMultiple size={18} /> },
    { to: "/assets", label: t("nav.item.assets"), icon: <IconBriefcase size={18} /> },
    { to: "/levels", label: t("nav.item.ratingLevels"), icon: <IconAdjustmentsHorizontal size={18} /> },
  ];
  const DOKUMENTATION: Item[] = [
    { to: "/dokumentation", label: t("nav.item.documentation"), icon: <IconFileText size={18} /> },
    { to: "/dokumentenlenkung", label: t("nav.item.documentControl"), icon: <IconBook size={18} /> },
  ];
  const ADMIN: Item[] = [
    { to: "/users", label: t("nav.item.users"), icon: <IconUsers size={18} />, adminOnly: true },
    { to: "/audit", label: t("nav.item.auditLog"), icon: <IconHistory size={18} />, adminOnly: true },
  ];

  return (
    <Stack gap={4} p="xs" h="100%">
      {ITEMS.map((i) => (
        <NavLink
          key={i.to}
          component={RouterLink}
          to={i.to}
          label={i.label}
          leftSection={i.icon}
          active={location.pathname === i.to}
        />
      ))}
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="md" px="sm">{t("nav.section.catalogues")}</Text>
      {CATALOGS.map((i) => (
        <NavLink key={i.to} component={RouterLink} to={i.to} label={i.label} leftSection={i.icon} active={location.pathname.startsWith(i.to)} />
      ))}
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="md" px="sm">{t("nav.section.documentation")}</Text>
      {DOKUMENTATION.map((i) => (
        <NavLink key={i.to} component={RouterLink} to={i.to} label={i.label} leftSection={i.icon} active={location.pathname.startsWith(i.to)} />
      ))}
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="md" px="sm">{t("nav.section.administration")}</Text>
      {ADMIN.filter(visible).map((i) => (
        <NavLink key={i.to} component={RouterLink} to={i.to} label={i.label} leftSection={i.icon} active={location.pathname.startsWith(i.to)} />
      ))}
      {/*
        Pinned to the foot of the navbar by mt="auto" against the h="100%" above,
        so it sits at the bottom of the sidebar rather than trailing the last
        nav item — which would move as items are added or hidden by role.
        Deliberately quiet: dimmed and one size smaller than the section labels,
        because it is here to be read when something goes wrong, not to compete
        with navigation. Not a link, and not translated — a version is a version
        in both languages.
      */}
      <Text size="xs" c="dimmed" mt="auto" px="sm" pt="md">
        v{APP_VERSION}
      </Text>
    </Stack>
  );
}
